import { Link } from '@cluey/cluey-components';
import { isEmpty, isUndefined, sortBy } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';

import { api } from '../../../../../api';
import { HubSessionListSkeletonItem } from '../../../../../components/hub/sessions/HubSessionsListItem/HubSessionListSkeletonItem';
import { ChevronDownIcon } from '../../../../../icons';
import { ENROLMENT_STATUS } from '../../../../../types/hubEnums';
import { formatError } from '../../../../../util/errors';
import { PATH_HUB_UPCOMING_SESSIONS } from '../../../../../util/pagePath';
import { useGetBulkSessionPolicy } from '../../../../../v2/common/hooks/useGetBulkSessionPolicy';
import { VoucherWidget } from '../../../../../v2/features/voucher';
import {
  SessionFilter,
  SessionFilterType,
  useSessionContext,
  withSessionContextProvider,
} from '../../../../../views/hub/Sessions';
import { hasCancelRequested, hasTrial } from './common';
import { EnrolmentSession, SnapshotUpcomingItem } from './enrolment-session';
import { AddAStudentCard, StudentCard, StudentCardSkeleton } from './student-card';

export const SnapshotStudents = () => {
  const [studentCursor, setStudentCursor] = useState(0);

  const {
    data: students,
    isLoading: isStudentsLoading,
    error: studentsError,
  } = api.student.all.useQuery(undefined, {
    select: (data) => sortBy(data, ['firstName', 'lastName']),
  });

  const studentsToShow = students?.slice(studentCursor, studentCursor + 3) || [];

  let desktop: ReactNode, mobile: ReactNode;

  if (studentsError) {
    // Error States
    desktop = (
      <div className="col-span-3 flex w-full flex-col gap-2 py-2">
        <div className="flex items-center justify-center">Something went wrong 😭</div>
        <pre className="text-center text-xs">{formatError(studentsError)}</pre>
      </div>
    );
    mobile = desktop;
  } else if (isStudentsLoading) {
    // Loading States
    desktop = (
      <>
        <StudentCardSkeleton />
        <StudentCardSkeleton />
        <StudentCardSkeleton />
      </>
    );
    mobile = <StudentCardSkeleton />;
  } else {
    // All is well, show content!
    desktop = (
      <>
        {studentCursor > 0 && (
          <div className="absolute right-full top-1/2 -translate-y-1/2 pr-5">
            <button
              type="button"
              onClick={() => setStudentCursor(studentCursor - 1)}
              className="flex h-10 w-10 items-center justify-center rounded border border-grey-2 hover:bg-slate-1"
            >
              <ChevronDownIcon className="rotate-90 text-link" />
              <span className="sr-only">Previous Student</span>
            </button>
          </div>
        )}
        {studentsToShow.map((student) => (
          <StudentCard key={student.studentId} student={student} />
        ))}
        {studentsToShow.length < 3 && <AddAStudentCard />}
        {studentsToShow.length > 2 && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 pl-5">
            <button
              type="button"
              onClick={() => setStudentCursor(studentCursor + 1)}
              className="flex h-10 w-10 items-center justify-center rounded border border-grey-2 hover:bg-slate-1"
            >
              <ChevronDownIcon className="-rotate-90 text-link" />
              <span className="sr-only">Next Student</span>
            </button>
          </div>
        )}
      </>
    );
    mobile = (
      <div className="flex snap-x gap-4 overflow-x-auto pb-4 md:hidden">
        {students?.map((student) => (
          <StudentCard key={student.studentId} student={student} className="snap-center" />
        ))}
        <AddAStudentCard />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-2xl font-bold tracking-[1px]">Students</h4>
      {/* Desktop View */}
      <div className="relative hidden grid-cols-3 gap-8 md:grid">{desktop}</div>
      {/* Mobile View */}
      <div className="relative h-36 md:hidden">{mobile}</div>
    </div>
  );
};

export const SnapshotAllStudents = () => {
  const {
    data: students,
    isLoading: isStudentsLoading,
    error: studentsError,
  } = api.student.all.useQuery(undefined, {
    select: (data) => sortBy(data, ['firstName', 'lastName']),
  });

  if (studentsError) {
    return (
      <div className="col-span-3 flex w-full flex-col gap-2 py-2">
        <div className="flex items-center justify-center">Something went wrong 😭</div>
        <pre className="text-center text-xs">{formatError(studentsError)}</pre>
      </div>
    );
  }

  if (isStudentsLoading) {
    return (
      <>
        <StudentCardSkeleton />
        <StudentCardSkeleton />
        <StudentCardSkeleton />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-2xl font-bold leading-7 tracking-normal text-grey-600">Students</h4>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {students.map((student) => (
          <StudentCard key={student.studentId} student={student} className="w-full md:w-auto" />
        ))}
        <AddAStudentCard className="w-full md:w-auto" />
      </div>
    </div>
  );
};

export const SnapshotSessions = () => {
  const { data: accountDetails } = api.account.details.useQuery();
  const { data: students, isLoading: isStudentsLoading, error: studentsError } = api.student.all.useQuery();
  const {
    filteredSessions,
    fetchSessionIsLoading: isSessionsLoading,
    fetchSessionIsError: sessionsError,
    sessions,
  } = useSessionContext();
  const { data: enrolments, isLoading: isLoadingEnrolments } = api.enrolment.all.useQuery('active');
  const { upcoming: upcomingSessions = [] } = filteredSessions;
  const { upcoming: allSessions = [] } = sessions;
  const { data: sessionPolicies, isLoading: isLoadingSessionPolicies } = useGetBulkSessionPolicy({
    sessionIds: upcomingSessions.map((session) => session.studentSessionId),
    enabled: !!upcomingSessions,
  });

  const upcomingItems = useMemo(() => {
    if (!upcomingSessions) return [];

    // in an emergency we can show the first 5 sessions
    if (!students) return upcomingSessions.slice(0, 5).map((session) => ({ type: 'session', session }));

    let withSessions: SnapshotUpcomingItem[] = [];
    let withoutSessions: SnapshotUpcomingItem[] = [];

    students.forEach((student) => {
      // flatten all enrolmentIds for the student
      const enrolmentIds = Object.values(student.enrolments).flat();

      // if there are no enrolments, show the student card
      if (enrolmentIds.length === 0) {
        withoutSessions.push({
          type: 'student',
          student,
        });
      } else {
        let missedEnrolments = 0;
        // get the next sessiopn for each of the student's enrolments
        enrolmentIds.forEach(({ uniqueEnrolmentId }) => {
          // fyi, upcomingSessions is sorted by select in useQuery
          const session = upcomingSessions.find((session) => session.uniqueEnrolmentId === uniqueEnrolmentId);
          if (session) {
            withSessions.push({
              type: 'session',
              policy: sessionPolicies?.[session.studentSessionId],
              session,
            });
          } else {
            // no upcoming sessions for this enrolment, check if it's active
            const enrolment = enrolments?.find((enrolment) => enrolment.enrolmentId === uniqueEnrolmentId);
            if (
              enrolment &&
              enrolment.enrolmentStatus !== ENROLMENT_STATUS.CANCELLED &&
              enrolment.enrolmentStatus !== ENROLMENT_STATUS.FINISHED &&
              (hasCancelRequested(enrolment) || hasTrial(enrolment))
            ) {
              withoutSessions.push({
                type: 'student',
                enrolment,
                student,
              });
            } else {
              // Check unfiltered sessions to see if the student has any active enrolments
              const checkAllSessions = allSessions.find((session) => session.uniqueEnrolmentId === uniqueEnrolmentId);
              if (!checkAllSessions) {
                // if we can't find any valid enrolments mark it as missed
                missedEnrolments++;
              }
            }
          }
        });

        if (missedEnrolments >= enrolmentIds.length) {
          // if all enrolments have no upcoming sessions or can't find a valid enrolment, show the student card
          withoutSessions.push({
            type: 'student',
            student,
          });
        }
      }
    });

    return [...sortBy(withSessions, ['session.startTime', 'session.student', 'session.subject']), ...withoutSessions];
  }, [upcomingSessions, students, enrolments, allSessions, sessionPolicies]);

  let view: ReactNode;
  if (sessionsError) {
    view = (
      <div className="col-span-3 flex w-full flex-col gap-2 py-2">
        <div className="flex items-center justify-center">Something went wrong 😭</div>
        <pre className="text-center text-xs">{formatError(studentsError || sessionsError)}</pre>
      </div>
    );
  } else if (isSessionsLoading || isLoadingEnrolments || isStudentsLoading || isLoadingSessionPolicies) {
    view = (
      <>
        <HubSessionListSkeletonItem />
        <HubSessionListSkeletonItem />
        <HubSessionListSkeletonItem />
      </>
    );
  } else if (isEmpty(upcomingItems)) {
    view = <p className="text-center">No upcoming sessions</p>;
  } else {
    view = upcomingItems.map((item) => (
      <EnrolmentSession
        key={item.type === 'session' ? item.session.studentSessionId : item.student.studentId}
        timezone={accountDetails.timezone}
        {...item}
      />
    ));
  }

  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-2xl font-bold tracking-[1px]">Upcoming sessions</h4>
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        {accountDetails ? (
          <p className="text-sm">Times are in your account timezone ({accountDetails.timezone})</p>
        ) : (
          <p className="animate-pulse rounded-full bg-slate-2 text-sm text-transparent">
            Times are in your account timezone
          </p>
        )}
        <Link size="base" to={PATH_HUB_UPCOMING_SESSIONS} className="link">
          View all sessions
        </Link>
      </div>
      {!isUndefined(students) && students.length > 1 && (
        <SessionFilter
          filterClassName="py-2"
          label="View by"
          labelFilter={false}
          labelClassName="font-bold font-[Proxima-Soft]"
          filterType={[SessionFilterType.STUDENT]}
          showClearFilter={false}
          filtersOccupyWidth={false}
          hideFiltersOnDefault={false}
        />
      )}
      <div className="flex flex-col gap-4">{view}</div>
    </div>
  );
};

export const SnapshotSessionsWithFilter = withSessionContextProvider(SnapshotSessions);

export const SnapshotWidget = () => {
  return (
    <section className="">
      <div className="container mr-auto px-0 pb-16 pt-10">
        <VoucherWidget />
        <div className="flex flex-col gap-16 rounded-3xl border border-slate-2 bg-white p-5 shadow-md md:p-10">
          <SnapshotSessionsWithFilter />
        </div>
        <div className="mt-10 flex flex-col gap-16 rounded-3xl border border-slate-2 bg-white p-5 shadow-md md:p-10">
          <SnapshotAllStudents />
        </div>
      </div>
    </section>
  );
};
